import type { Id } from "@convex/_generated/dataModel";

import { toConvexTableId } from "~/lib/convex-table-id";

export interface ReasonTracking {
  reason: string;
  amount: string;
  permissionCode: {
    employeeId: string;
    employeeName: string;
  };
}

export interface SalesSummary {
  categoryId: string;
  categoryName: string;
  amount: string;
  quantity: number;
}

export interface DeviceTransaction {
  cashCollected: string;
  cashSales: string;
  cashDeposits: string;
  cashWithdrawals: string;
  difference: string;
  inputByEmployeeId: string | null;
}

export interface EmployeeReportSettings {
  showEmployeeHours: boolean;
  showEmployeeTips: boolean;
  showOrdersAndBillsSummary: boolean;
  showSalesCategoriesSummary: boolean;
  showDevicesSummary: boolean;
  showVoidedItems: boolean;
  showDiscountedItems: boolean;
  showRefundedItems: boolean;
  showManagerCodes: boolean;
  reprintEmployeeReportWhenClockingOut: boolean;
  showDividers: boolean;
  printerDeviceId: string;
  printAutomaticallyOnClockOut: boolean;
  printCopies: number;
}

export function isEmployeeReportSettings(
  value: unknown
): value is EmployeeReportSettings {
  return (
    typeof value === "object" && value !== null && "showEmployeeHours" in value
  );
}

export function toPrinterDeviceId(value: string): "all" | Id<"printers"> {
  if (value === "all") {
    return "all";
  }
  return toConvexTableId("printers", value);
}

// Type for employee report data from backend (may have number fields)
export interface EmployeeReportDataFromBackend {
  employeeId?: string | null;
  hoursWorked?: number;
  weekHours?: number;
  cashTips?: number;
  cardTips?: number;
  totalTips?: number;
  ordersCreated?: number;
  billsClosed?: number;
  averageBillAmount?: number;
  totalSales?: number;
  startCash?: number;
  salesByCategory?: SalesSummary[];
  deviceTransactions?: DeviceTransaction | null;
  voidedItems?: ReasonTracking[];
  discountedItems?: ReasonTracking[];
  refundedItems?: ReasonTracking[];
}

// Type for display data (with string values for formatting)
export interface EmployeeReportData {
  employeeId: string | null;
  hoursWorked: string;
  weekHours: string;
  cashTips: string;
  cardTips: string;
  totalTips: string;
  ordersCreated: number;
  billsClosed: number;
  averageBillAmount: string;
  totalSales: string;
  startCash: string;
  salesByCategory: SalesSummary[];
  deviceTransactions: DeviceTransaction | null;
  voidedItems: ReasonTracking[];
  discountedItems: ReasonTracking[];
  refundedItems: ReasonTracking[];
}

// Helper interface for employee report visibility settings
export interface EmployeeVisibilitySettings {
  showSalesCategoriesSummary: boolean;
  showDevicesSummary: boolean;
  showVoidedItems: boolean;
  showDiscountedItems: boolean;
  showRefundedItems: boolean;
}

// Helper to calculate total amount from ReasonTracking items
export function calculateItemsTotal(items: ReasonTracking[]): string {
  return items.reduce((sum, item) => sum + Number(item.amount), 0).toFixed(2);
}

// Helper to check if employee report has real data
export function checkEmployeeReportHasRealData(
  report: EmployeeReportDataFromBackend | null | undefined
): boolean {
  if (!report) {
    return false;
  }

  const hasHoursOrTips =
    (report.hoursWorked ?? 0) > 0 ||
    (report.weekHours ?? 0) > 0 ||
    (report.cashTips ?? 0) > 0 ||
    (report.cardTips ?? 0) > 0 ||
    (report.totalTips ?? 0) > 0;
  const hasOrders =
    (report.ordersCreated ?? 0) > 0 || (report.billsClosed ?? 0) > 0;
  const hasSalesCategories = (report.salesByCategory ?? []).length > 0;
  const hasDeviceTransactions = Boolean(report.deviceTransactions ?? false);
  const hasVoidedItems = (report.voidedItems ?? []).length > 0;
  const hasDiscountedItems = (report.discountedItems ?? []).length > 0;
  const hasRefundedItems = (report.refundedItems ?? []).length > 0;

  return (
    hasHoursOrTips ||
    hasOrders ||
    hasSalesCategories ||
    hasDeviceTransactions ||
    hasVoidedItems ||
    hasDiscountedItems ||
    hasRefundedItems
  );
}

// Helper to format backend report data to display data
export function formatEmployeeReportForDisplay(
  report: EmployeeReportDataFromBackend,
  employeeId: string | null
): EmployeeReportData {
  return {
    employeeId: report.employeeId ?? employeeId,
    hoursWorked: (report.hoursWorked ?? 0).toFixed(2),
    weekHours: (report.weekHours ?? 0).toFixed(2),
    cashTips: (report.cashTips ?? 0).toFixed(2),
    cardTips: (report.cardTips ?? 0).toFixed(2),
    totalTips: (report.totalTips ?? 0).toFixed(2),
    ordersCreated: report.ordersCreated ?? 0,
    billsClosed: report.billsClosed ?? 0,
    averageBillAmount: (report.averageBillAmount ?? 0).toFixed(2),
    totalSales: (report.totalSales ?? 0).toFixed(2),
    startCash: (report.startCash ?? 0).toFixed(2),
    salesByCategory: report.salesByCategory ?? [],
    deviceTransactions: report.deviceTransactions ?? null,
    voidedItems: report.voidedItems ?? [],
    discountedItems: report.discountedItems ?? [],
    refundedItems: report.refundedItems ?? [],
  };
}

// Helper to compute employee report visibility conditions
export function computeEmployeeVisibility(
  settings: EmployeeVisibilitySettings,
  isPreviewData: boolean,
  displayData: EmployeeReportData
) {
  return {
    shouldShowSalesCategories:
      settings.showSalesCategoriesSummary &&
      (isPreviewData || displayData.salesByCategory),
    shouldShowDevices:
      settings.showDevicesSummary &&
      (isPreviewData || displayData.deviceTransactions) &&
      displayData.deviceTransactions,
    shouldShowVoided:
      settings.showVoidedItems &&
      (isPreviewData || Boolean(displayData.voidedItems?.length)) &&
      displayData.voidedItems,
    shouldShowDiscounted:
      settings.showDiscountedItems &&
      (isPreviewData || Boolean(displayData.discountedItems?.length)) &&
      displayData.discountedItems,
    shouldShowRefunded:
      settings.showRefundedItems &&
      (isPreviewData || Boolean(displayData.refundedItems?.length)) &&
      displayData.refundedItems,
  };
}
